import { Module } from 'vuex'
import { RootState } from 'surface/xstore/types'
import { Post, PostAttributes } from 'types'

const HEADER_OFFSET = 1
const ADD_BUTTON_OFFSET = 1
const ZERO_INDEX_OFFSET = 1
const flatten = (arr: any[]): any[] => [].concat(...arr)

// TODO: Don't use left and top
const getRowNumber = (post: PostAttributes): number => post.top || 0
const getColNumber = (post: PostAttributes): number => post.left || 0
const setRowNumber = (post: PostAttributes, rowNumber: number): void => { post.top = rowNumber }
const setColNumber = (post: PostAttributes, colNumber: number): void => { post.left = colNumber }
const hasNoAssignedCell = (post: PostAttributes): boolean => !post.left || !post.top || (post.left > 64 && post.top > 64) // Temp
const hasAssignedCell = (post: PostAttributes): boolean => !hasNoAssignedCell(post) // Temp

interface CellData {
  row: number,
  col: number,
  key: string,
  isBlank?: boolean,
  isPlaceholder?: boolean,
  isHeader?: boolean,
  isRowHeader?: boolean,
  isColHeader?: boolean,
  isPost?: boolean,
  isUploading?: boolean,
  isLastPost?: boolean,
  isAddColButton?: boolean,
  isAddRowButton?: boolean,
  post?: Post,
  text?: string
}

interface TableState {
  forcedShownRows: number
  forcedShownCols: number
  cellBeingMoved: CellData | null
  openHeaderActionMenuKey: string | null
}
const defaultState = (): TableState => ({
  forcedShownRows: 1,
  forcedShownCols: 1,
  cellBeingMoved: null,
  openHeaderActionMenuKey: null,
})

const TableModule: Module<TableState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    posts (state, getters, rootState, rootGetters): Post[] {
      return rootGetters.isTable ? rootGetters['post/postsArray'] : []
    },
    pendingPosts (state, getters, rootState, rootGetters): Post[] {
      return rootGetters.isTable ? rootGetters['post/pendingPosts'] : []
    },
    cellBeingMoved (state): CellData | null {
      return state.cellBeingMoved
    },
    rowBeingMoved (state): number | null {
      const isRowBeingMoved = state.cellBeingMoved && state.cellBeingMoved.isRowHeader
      return isRowBeingMoved ? (state.cellBeingMoved as CellData).row : null
    },
    colBeingMoved (state): number | null {
      const isColBeingMoved = state.cellBeingMoved && state.cellBeingMoved.isColHeader
      return isColBeingMoved ? (state.cellBeingMoved as CellData).col : null
    },
    postBeingMoved (state, getters): Post | null {
      return (getters.cellBeingMoved && getters.cellBeingMoved.post) || null
    },
    isPostBeingMoved (state, getters): boolean {
      return !!getters.postBeingMoved
    },
    celledCreatedPosts (state, getters): Post[] {
      return getters.posts.filter(hasAssignedCell)
    },
    celllessCreatedPosts (state, getters): Post[] {
      return getters.posts.filter(hasNoAssignedCell)
    },
    celllessPendingPosts (state, getters): Post[] {
      return getters.pendingPosts.filter(hasNoAssignedCell)
    },
    postsPendingUpload (state, getters): Post[] {
      return getters.pendingPosts.filter(hasAssignedCell)
    },
    celledPosts (state, getters): Post[] {
      return [...getters.celledCreatedPosts, ...getters.postsPendingUpload]
    },
    maxPostsRows (state, getters): number {
      return Math.max(...getters.celledPosts.map(getRowNumber), 1)
    },
    maxPostsCols (state, getters): number {
      return Math.max(...getters.celledPosts.map(getColNumber), 1)
    },
    isSelectingCellForPendingPost (state, getters): boolean {
      return getters.celllessPendingPosts.length > 0
    },
    isSelectingCellForCreatedPost (state, getters): boolean {
      return !getters.isSelectingCellForPendingPost && getters.celllessCreatedPosts.length > 0
    },
    isSelectingCell (state, getters): boolean {
      return getters.isPostBeingMoved || getters.isSelectingCellForPendingPost || getters.isSelectingCellForCreatedPost
    },
    postToSelectCellFor (state, getters): Post | null {
      if (getters.isPostBeingMoved) return getters.postBeingMoved
      if (getters.isSelectingCellForPendingPost) return getters.celllessPendingPosts[0]
      if (getters.isSelectingCellForCreatedPost) return getters.celllessCreatedPosts[0]
      return null
    },
    xBlankFringeCells (state, getters): boolean {
      return getters.isSelectingCellForPendingPost || getters.isSelectingCellForCreatedPost
    },
    rows (state, getters): number {
      const postRows = getters.maxPostsRows + (getters.xBlankFringeCells ? 1 : 0)
      return Math.max(state.forcedShownRows, postRows)
    },
    cols (state, getters): number {
      const postCols = getters.maxPostsCols + (getters.xBlankFringeCells ? 1 : 0)
      return Math.max(state.forcedShownCols, postCols)
    },
    cells (state, getters): CellData[] {
      const totalRows = HEADER_OFFSET + getters.rows + ADD_BUTTON_OFFSET
      const totalCols = HEADER_OFFSET + getters.cols + ADD_BUTTON_OFFSET
      const contentMatrix: CellData[][] = Array(totalRows).fill(null).map((_r, row): CellData[] => (
        Array(totalCols).fill(null).map((_c, col): CellData => (
          { row, col, key: `placeholder-${row}-${col}`, isPlaceholder: true }
        ))
      ))

      const posts = getters.celledCreatedPosts
      posts.forEach((post: Post, index): void => {
        const row = getRowNumber(post)
        const col = getColNumber(post)
        const isLastPost = index === posts.length - 1
        contentMatrix[row][col] = { row, col, key: `post-${post.cid}`, isPost: true, post, isLastPost }
      })

      getters.postsPendingUpload.forEach((post: Post): void => {
        const row = getRowNumber(post)
        const col = getColNumber(post)
        contentMatrix[row][col] = { row, col, key: `uploading-${row}-${col}`, isUploading: true, post }
      })

      Array(getters.rows).fill(null).forEach((_, index): void => {
        const row = HEADER_OFFSET + index
        const col = 0
        const text = row.toString()
        contentMatrix[row][col] = { row, col, key: `header-${row}-${col}`, isHeader: true, isRowHeader: true, text }
      })
      Array(getters.cols).fill(null).forEach((_, index): void => {
        const row = 0
        const col = HEADER_OFFSET + index
        const text = String.fromCharCode(65 + index)
        contentMatrix[row][col] = { row, col, key: `header-${row}-${col}`, isHeader: true, isColHeader: true, text }
      })

      contentMatrix[0][0] = { row: 0, col: 0, isBlank: true, key: 'blank-0-0' }
      Array(getters.rows + ADD_BUTTON_OFFSET).fill(null).forEach((_, index): void => {
        const row = HEADER_OFFSET + index
        const col = totalCols - ZERO_INDEX_OFFSET
        contentMatrix[row][col] = { row, col, key: `blank-${row}-${col}`, isBlank: true }
      })
      Array(getters.cols).fill(null).forEach((_, index): void => {
        const row = totalRows - ZERO_INDEX_OFFSET
        const col = HEADER_OFFSET + index
        contentMatrix[row][col] = { row, col, key: `blank-${row}-${col}`, isBlank: true }
      })

      const addColButtonData = { row: 0, col: totalCols - ZERO_INDEX_OFFSET, isAddColButton: true, key: 'add-col' }
      const addRowButtonData = { row: totalRows - ZERO_INDEX_OFFSET, col: 0, isAddRowButton: true, key: 'add-row' }
      contentMatrix[0][addColButtonData.col] = addColButtonData
      contentMatrix[addRowButtonData.row][0] = addRowButtonData

      return flatten(contentMatrix)
    },
    canCancelSelectCell (state, getters): boolean {
      return getters.isSelectingCellForPendingPost
    },
    canRearrangeTable (state, getters, rootState, rootGetters): boolean {
      return rootGetters.isPostable
    },
    isHeaderActionMenuOpen (state): (headerKey: string) => boolean {
      return (headerKey): boolean => state.openHeaderActionMenuKey === headerKey
    },
  },
  mutations: {
    FORCE_SHOW_ROWS (state, rows: number): void {
      state.forcedShownRows = rows
    },
    FORCE_SHOW_COLS (state, cols: number): void {
      state.forcedShownCols = cols
    },
    START_MOVING_CELL (state, cell: CellData): void {
      state.cellBeingMoved = cell
    },
    STOP_MOVING_CELL (state): void {
      state.cellBeingMoved = null
    },
    OPEN_HEADER_ACTION_MENU (state, headerKey): void {
      state.openHeaderActionMenuKey = headerKey
    },
    CLOSE_HEADER_ACTION_MENU (state, headerKey): void {
      if (state.openHeaderActionMenuKey === headerKey) {
        state.openHeaderActionMenuKey = null
      }
    },
  },
  actions: {
    showAdditionalRow ({ commit, getters }): void {
      commit('FORCE_SHOW_ROWS', getters.rows + 1)
    },
    showAdditionalCol ({ commit, getters }): void {
      commit('FORCE_SHOW_COLS', getters.cols + 1)
    },
    startNewPostInCell ({ dispatch }, { row, col }: CellData): void {
      const attributes: PostAttributes = {}
      setRowNumber(attributes, row)
      setColNumber(attributes, col)
      dispatch('post/startNewPost', { attributes }, { root: true })
    },
    cancelSelectCellForPendingPost ({ dispatch, getters }): void {
      const post = getters.celllessPendingPosts.length > 0 && getters.celllessPendingPosts[0]
      if (post) dispatch('post/removePendingPost', post, { root: true })
    },
    cancelSelectCell ({ dispatch, getters }): void {
      if (getters.isSelectingCellForPendingPost) dispatch('cancelSelectCellForPendingPost')
    },
    putPendingPostInCell ({ dispatch }, { post: { cid }, cell }): void {
      const attributes = { cid }
      setRowNumber(attributes, cell.row)
      setColNumber(attributes, cell.col)
      dispatch('post/updatePendingPost', attributes, { root: true })
    },
    putPostInCell ({ dispatch }, { post, cell }): void {
      const attributes = {}
      setRowNumber(attributes, cell.row)
      setColNumber(attributes, cell.col)
      // TODO: fix this hack
      const payload = { postCid: post.cid, position: attributes }
      dispatch('post/movedPost', payload, { root: true })
    },
    selectCell ({ dispatch, getters }, cell: CellData): void {
      const post = getters.postToSelectCellFor as Post
      if (getters.isPostBeingMoved) dispatch('putPostInCell', { post, cell })
      if (getters.isSelectingCellForPendingPost) dispatch('putPendingPostInCell', { post, cell })
      if (getters.isSelectingCellForCreatedPost) dispatch('putPostInCell', { post, cell })
    },
    startMovingCell ({ commit }, cell: CellData): void {
      commit('START_MOVING_CELL', cell)
    },
    stopMovingCell ({ commit }): void {
      commit('STOP_MOVING_CELL')
    },
    moveRow (_, { sourceRow, rowToMoveAfter }): void {
      if (rowToMoveAfter === sourceRow || rowToMoveAfter === sourceRow - 1) return
      console.info(`Move row ${sourceRow} after row ${rowToMoveAfter}`) // TODO
    },
    moveCol (_, { sourceCol, colToMoveAfter }): void {
      if (colToMoveAfter === sourceCol || colToMoveAfter === sourceCol - 1) return
      console.info(`Move col ${sourceCol} after col ${colToMoveAfter}`) // TODO
    },
    openHeaderActionMenu ({ commit }, headerKey): void {
      commit('OPEN_HEADER_ACTION_MENU', headerKey)
    },
    closeHeaderActionMenu ({ commit }, headerKey): void {
      commit('CLOSE_HEADER_ACTION_MENU', headerKey)
    },
  },
}
export default TableModule
export {
  TableState,
  CellData,
}
